import type { AuditTargetDescription } from './target';
import { auditMessage } from './text';
import type { AuditLogEntry, AuditScopeKind, AuditScopeRef } from './types';

// The backend writes labels with at most this many characters.
const LABEL_MAX_LENGTH = 255;

/** The label the write path would build today: `<site> / <pen>` for a pen. */
function currentLabel(
    entry: AuditLogEntry,
    kind: AuditScopeKind,
    ref: AuditScopeRef
): string | null {
    if (ref.name === null) return null;
    if (kind !== 'cage' || entry.location === null) return ref.name;
    return entry.location.name === null ? null : `${entry.location.name} / ${ref.name}`;
}

export function renamed(
    entry: AuditLogEntry,
    kind: AuditScopeKind,
    ref: AuditScopeRef
): AuditTargetDescription['renamed'] {
    const recorded = entry.entityLabel;
    const current = currentLabel(entry, kind, ref);
    if (recorded === null || current === null || recorded === current) return null;
    // A label cut at the write-time cap is not a rename.
    if (recorded.length === LABEL_MAX_LENGTH && current.startsWith(recorded)) return null;
    return {
        marker: auditMessage('auditLog.target.renamed'),
        title: auditMessage('auditLog.target.recordedAs', { label: recorded }),
    };
}

/**
 * The write path puts the scope in front of a label: `<site> / <pen>` for a pen, and
 * `<site> / <pen>: <name>` for what sits in a pen. The path line already shows that
 * scope, so the name keeps only its own part. The prefix is matched against the scope's
 * names as they are now, so a label written under names changed since stays whole
 * rather than being cut in the wrong place.
 */
function ownPart(entry: AuditLogEntry, label: string): string {
    const site = entry.location?.name ?? null;
    const pen = entry.cage?.name ?? null;
    if (pen !== null) {
        const nested = site === null ? `${pen}: ` : `${site} / ${pen}: `;
        if (label.startsWith(nested) && label.length > nested.length) {
            return label.slice(nested.length);
        }
    }
    const prefix = `${site} / `;
    if (entry.entityType !== 'cage' || site === null || !label.startsWith(prefix)) return label;
    // A bulk creation lists each new pen with the site in front of it.
    return label.slice(prefix.length).split(`, ${prefix}`).join(', ');
}

export function recordedName(
    entry: AuditLogEntry
): Pick<AuditTargetDescription, 'name' | 'recordedLabel'> {
    const label = entry.entityLabel;
    if (label !== null) {
        const own = ownPart(entry, label);
        return { name: own, recordedLabel: own === label ? null : label };
    }
    const name =
        entry.entityId === null
            ? null
            : auditMessage('auditLog.target.entityId', { id: entry.entityId });
    return { name, recordedLabel: null };
}
